/**
 * Client-chunked multipart uploader for large files.
 *
 *   uploadFileMultipart(file, {
 *     jobId, folder, apiBase, axiosInstance,
 *     partSize: 8 * 1024 * 1024,      // default 8 MB
 *     concurrency: 4,                 // parts in flight simultaneously
 *     onProgress: ({loaded, total, percent}) => void,
 *     signal: AbortSignal,            // to cancel
 *   }) → Promise<{filename, size}>
 *
 * Strategy:
 *   1) POST /files/multipart/init  → upload_id + collision-safe filename
 *   2) Slice the File into N parts.  Upload up to `concurrency` parts
 *      in parallel.  Each part POSTs to /files/multipart/part with its
 *      part_number and returns an ETag.
 *   3) Retry per part: 3 attempts with exp back-off (400 ms → 3 s).
 *   4) When all ETags collected, POST /files/multipart/complete.
 *   5) On any unrecoverable error or AbortSignal, POST /multipart/abort.
 *
 * The chunk requests are small (≤ 8 MB) — the ingress proxy always
 * finishes them, so a 1 GB file no longer needs one 20-min connection
 * to survive.
 */

const MAX_PART_RETRIES = 3;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * PUT a blob to a presigned URL with real upload progress. Uses
 * XMLHttpRequest because fetch() still can't expose upload progress
 * cross-browser in 2026.
 *
 * Returns the ETag header the browser saw from R2 — needed by the
 * subsequent /multipart/complete call to stitch the parts. The R2
 * bucket must expose ETag via CORS `ExposeHeaders` (done as part of
 * the setup step).
 */
function putBlobToPresigned(url, blob, { onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    if (signal) {
      if (signal.aborted) {
        try { xhr.abort(); } catch { /* ignore */ }
        reject(new DOMException("aborted", "AbortError"));
        return;
      }
      signal.addEventListener("abort", () => {
        try { xhr.abort(); } catch { /* ignore */ }
      }, { once: true });
    }
    xhr.upload.onprogress = (e) => {
      if (!e || !e.lengthComputable) return;
      onProgress && onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // R2 exposes the ETag header via CORS; strip surrounding quotes.
        const raw = xhr.getResponseHeader("ETag")
          || xhr.getResponseHeader("etag")
          || "";
        resolve(raw.replace(/^"|"$/g, ""));
      } else {
        reject(new Error(`R2 PUT failed: HTTP ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error("R2 PUT network error"));
    xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));
    xhr.send(blob);
  });
}

async function uploadOnePart({
  axiosInstance, apiBase, jobId, folder, filename, uploadId,
  partNumber, blob, onDelta, signal,
}) {
  let lastReported = 0;
  for (let attempt = 1; attempt <= MAX_PART_RETRIES; attempt += 1) {
    try {
      // 1) Ask the backend for a short-lived presigned PUT for this
      //    part. Zero bytes flow through our backend here.
      const { data: signed } = await axiosInstance.post(
        `${apiBase}/jobs/${jobId}/files/multipart/presign-part`,
        {
          folder, filename, upload_id: uploadId,
          part_number: partNumber,
        },
        { signal, timeout: 30 * 1000 },
      );

      // 2) PUT the chunk directly to Cloudflare R2's edge.
      const etag = await putBlobToPresigned(signed.url, blob, {
        signal,
        onProgress: (loaded) => {
          const delta = loaded - lastReported;
          lastReported = loaded;
          if (delta > 0) onDelta(delta);
        },
      });
      if (!etag) {
        throw new Error(
          "R2 did not return an ETag — check the bucket's CORS "
          + "ExposeHeaders includes \"ETag\".",
        );
      }
      // Snap to full size in case the last progress event was < total.
      if (lastReported < blob.size) onDelta(blob.size - lastReported);
      return { part_number: partNumber, etag };
    } catch (err) {
      if (signal?.aborted) throw err;
      // Roll progress back to zero for this part so the next attempt
      // reports its own loaded bytes cleanly.
      if (lastReported > 0) {
        onDelta(-lastReported);
        lastReported = 0;
      }
      if (attempt === MAX_PART_RETRIES) throw err;
      await sleep(400 * 2 ** (attempt - 1));  // 400 ms → 800 ms → 1.6 s
    }
  }
  throw new Error(`Part ${partNumber} exhausted retries`);
}

export async function uploadFileMultipart(file, opts) {
  const {
    jobId, folder, apiBase, axiosInstance,
    partSize = 8 * 1024 * 1024,
    concurrency = 4,
    onProgress,
    signal,
    relativePath,   // optional nested path, e.g. "Ceremony/photo1.jpg"
  } = opts;

  // Prefer explicit relativePath, else the browser-provided webkitRelativePath
  // (set on <input webkitdirectory>), else the plain file name.
  const initName = relativePath || file.webkitRelativePath || file.name;

  // 1) Init
  const { data: init } = await axiosInstance.post(
    `${apiBase}/jobs/${jobId}/files/multipart/init`,
    {
      filename: initName,
      folder,
      content_type: file.type || "application/octet-stream",
    },
    { signal, timeout: 60 * 1000 },
  );
  const uploadId = init.upload_id;
  const filename = init.filename;
  const size = file.size;
  const partCount = Math.max(1, Math.ceil(size / partSize));
  if (partCount > init.max_parts) {
    // Shouldn't happen — 10000 parts × 8 MB = 80 GB. But guard anyway.
    throw new Error(`File too large: needs ${partCount} parts (max ${init.max_parts})`);
  }

  // 2) Fan-out chunk uploads with concurrency limit
  let loaded = 0;
  const report = () => {
    onProgress && onProgress({
      loaded: Math.max(0, Math.min(size, loaded)),
      total: size,
      percent: size ? Math.min(100, Math.round((loaded / size) * 100)) : 100,
    });
  };
  const onDelta = (d) => { loaded += d; report(); };

  const partsMeta = new Array(partCount);
  let nextPart = 0;

  const worker = async () => {
    // Pull the next part number, upload it, repeat.
    while (true) {
      if (signal?.aborted) throw new Error("aborted");
      const p = nextPart++;
      if (p >= partCount) return;
      const start = p * partSize;
      const end = Math.min(size, start + partSize);
      const blob = file.slice(start, end);
      partsMeta[p] = await uploadOnePart({
        axiosInstance, apiBase, jobId, folder,
        filename, uploadId,
        partNumber: p + 1,
        blob,
        onDelta,
        signal,
      });
    }
  };

  try {
    await Promise.all(Array.from({ length: Math.min(concurrency, partCount) }, worker));
  } catch (err) {
    // Best-effort abort to release parts on R2. Don't throw from abort.
    try {
      await axiosInstance.post(
        `${apiBase}/jobs/${jobId}/files/multipart/abort`,
        { filename, folder, upload_id: uploadId },
        { timeout: 30 * 1000 },
      );
    } catch { /* ignore */ }
    throw err;
  }

  // 3) Complete
  const { data: done } = await axiosInstance.post(
    `${apiBase}/jobs/${jobId}/files/multipart/complete`,
    { filename, folder, upload_id: uploadId, parts: partsMeta },
    { signal, timeout: 5 * 60 * 1000 },
  );
  // Snap progress to 100 in case rounding left it at 99.
  loaded = size;
  report();
  return {
    filename: done.filename,
    size: done.size,
    cdn_url: done.cdn_url || null,
  };
}

/**
 * Upload a list of files with per-file multipart when needed, and a
 * queue limiting how many files run in parallel.
 *
 * Files below `singleShotThreshold` use the existing single-POST route
 * (faster round-trip for typical JPEGs).  Files at/above use multipart.
 */
export async function uploadFilesQueued(files, opts) {
  const {
    jobId, folder, apiBase, axiosInstance,
    // Direct-to-R2 uploads: every file goes via the multipart path so
    // its bytes never touch our backend proxy. Even a 100 KB thumbnail
    // is one part → one presigned PUT → done, so the "single-shot"
    // path is no longer needed. Kept as a knob purely for tests /
    // debugging that want to force the legacy proxy path (>=1).
    singleShotThreshold = 0,
    fileConcurrency = 4,
    retryFailed = true,       // auto-retry each failed file once after the main pass
    onFileProgress,       // ({index, file, loaded, total, percent, phase})
    onFileDone,           // ({index, file, result})
    onFileError,          // ({index, file, error})
    onPhaseChange,        // ("uploading"|"retrying")
    signal,
  } = opts;

  const uploadSingle = async (file, index) => {
    const fd = new FormData();
    fd.append("folder", folder);
    fd.append("files", file);
    // Preserve nested folder structure when a webkitdirectory input
    // (or a drag-dropped folder) supplied a relative path. Sent as a
    // JSON string so the backend can parse it into List[str] without
    // Pydantic v2 refusing a scalar-→list coercion.
    const rel = file.webkitRelativePath || file.name;
    fd.append("paths_json", JSON.stringify([rel]));
    onFileProgress && onFileProgress({
      index, file, loaded: 0, total: file.size, percent: 0, phase: "uploading",
    });
    const { data } = await axiosInstance.post(
      `${apiBase}/jobs/${jobId}/files`, fd,
      {
        signal,
        timeout: 10 * 60 * 1000,
        onUploadProgress: (e) => {
          if (!e.total) return;
          const percent = Math.round((e.loaded / e.total) * 100);
          onFileProgress && onFileProgress({
            index, file, loaded: e.loaded, total: e.total, percent,
            phase: percent >= 100 ? "saving" : "uploading",
          });
        },
      },
    );
    const saved = (data && data.saved && data.saved[0]) || {};
    return { filename: saved.filename || rel, size: saved.size ?? file.size };
  };

  const uploadOne = async (file, index) => {
    if (file.size < singleShotThreshold) {
      return uploadSingle(file, index);
    }
    return uploadFileMultipart(file, {
      jobId, folder, apiBase, axiosInstance, signal,
      relativePath: file.webkitRelativePath || file.name,
      onProgress: ({ loaded, total, percent }) => {
        onFileProgress && onFileProgress({
          index, file, loaded, total, percent,
          phase: percent >= 100 ? "saving" : "uploading",
        });
      },
    });
  };

  let next = 0;
  const results = new Array(files.length);
  const runIndex = async (i, { isRetry = false } = {}) => {
    if (signal?.aborted) return;
    const f = files[i];
    try {
      const r = await uploadOne(f, i);
      results[i] = { ok: true, result: r };
      onFileDone && onFileDone({ index: i, file: f, result: r, isRetry });
    } catch (err) {
      results[i] = { ok: false, error: err };
      onFileError && onFileError({ index: i, file: f, error: err, isRetry });
    }
  };
  const worker = async () => {
    while (true) {
      if (signal?.aborted) return;
      const i = next++;
      if (i >= files.length) return;
      await runIndex(i);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(fileConcurrency, files.length) }, worker),
  );

  // ── auto-retry pass ──────────────────────────────────────────────
  // Any file that failed the first time gets one more shot — a common
  // pattern for large batches where a single R2 hiccup or a transient
  // 401 (already refreshed by the axios interceptor) is enough to fail
  // a file. This runs sequentially so it doesn't contend with any
  // in-flight retries.
  if (retryFailed && !signal?.aborted) {
    const failedIdx = results
      .map((r, i) => (r && !r.ok ? i : -1))
      .filter((i) => i >= 0);
    if (failedIdx.length > 0) {
      onPhaseChange && onPhaseChange("retrying");
      for (const i of failedIdx) {
        if (signal?.aborted) break;
        // Reset the slot so onFileDone/onFileError semantics stay clean.
        results[i] = undefined;
        await runIndex(i, { isRetry: true });
      }
      onPhaseChange && onPhaseChange("uploading");
    }
  }

  return results;
}
